import {
  ActionTask,
  CodeTask,
  Conditional,
  DataConnector,
  ParameterReference,
  ReferenceData,
  ScriptFunctionInfo,
  StaticData,
  TaskOutputReference,
  WorkcellInstrument,
  Workflow,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';

import {
  getFlatWorkflowTasks,
  getSourceHandleName,
  getTargetHandleName,
  validateInputId,
} from './utils';
import { DriversByNameAndVersion } from '../../../../data/drivers';
import {
  SDKInteropError,
  SdkMisconfigurationError,
  UnsupportedFeatureError,
} from '../../../../errors';
import { getUniqueId, toDataHandleId } from '../../../../features/helpers';
import { GraphEdges } from '../../../../types';
import { TaskType } from '../../types';

type Inputs = Record<
  string,
  StaticData | TaskOutputReference | ReferenceData | ParameterReference
>;

const getEdgesFromInputs = (
  inputs: Inputs,
  workflowFlatTasksById: Record<string, TaskType>,
  task: TaskType,
  functions: ScriptFunctionInfo[],
  workcellInstruments: WorkcellInstrument[],
  driversByNameAndVersion: DriversByNameAndVersion
): GraphEdges => {
  return Object.entries(inputs).reduce<GraphEdges>(
    (graphEdges, [inputId, input]) => {
      if ('task_id' in input) {
        input satisfies TaskOutputReference;

        const sourceTask = workflowFlatTasksById[input.task_id];
        if (!sourceTask) {
          throw new SdkMisconfigurationError(
            `Data dependency input for task "${task.id}" references non-existent source task "${input.task_id}"`
          );
        }

        const source = input.task_id;
        const target = task.id;
        const sourceHandleName = getSourceHandleName(
          sourceTask,
          input,
          workcellInstruments,
          driversByNameAndVersion
        );

        // throws SdkMisconfigurationError if invalid
        validateInputId(inputId, task, functions);

        const targetHandleName = getTargetHandleName(inputId, task.type);

        return [
          ...graphEdges,
          {
            id: getUniqueId({
              type: 'dataDependencyEdge',
              message: `from-${source}-to-${target}`,
            }),
            source,
            target,
            sourceHandle: toDataHandleId('source', source, sourceHandleName), // source task
            targetHandle: toDataHandleId('target', target, targetHandleName), // this task
            type: 'dataDependency',
          },
        ];
      } else if ('parameter_id' in input) {
        input satisfies ParameterReference;
        // ParameterReference inputs don't have edges
        return graphEdges;
      } else if ('value' in input) {
        input satisfies StaticData;
        // static data inputs don't have edges
        return graphEdges;
      } else if ('key' in input) {
        input satisfies ReferenceData;
        throw new UnsupportedFeatureError(
          'ReferenceData task inputs are not supported in Canvas'
        );
      } else {
        input satisfies never;
        throw new SDKInteropError('This should be unreachable');
      }
    },
    []
  );
};

export const getDataDependencyEdges = (
  workflow: Workflow,
  functions: ScriptFunctionInfo[],
  workcellInstruments: WorkcellInstrument[],
  driversByNameAndVersion: DriversByNameAndVersion
): GraphEdges => {
  const graphEdges: GraphEdges = [];
  const workflowFlatTasksById = getFlatWorkflowTasks(workflow.tasks);

  for (const task of Object.values(workflowFlatTasksById)) {
    if ('inputs' in task) {
      task satisfies ActionTask | CodeTask | DataConnector;

      const { inputs } = task;

      graphEdges.push(
        ...getEdgesFromInputs(
          inputs,
          workflowFlatTasksById,
          task,
          functions,
          workcellInstruments,
          driversByNameAndVersion
        )
      );
    }

    if ('comparison' in task) {
      task satisfies Conditional;

      const { comparison } = task;

      if (comparison === 'Else') continue;

      const comparisonInputs: Inputs = {
        target_data: comparison.rhs, // "target_data" key name is not currently important, just used for symmetry
        source_data: comparison.lhs, // "source_data" key name is important - it is used to match the handle name
      };

      graphEdges.push(
        ...getEdgesFromInputs(
          comparisonInputs,
          workflowFlatTasksById,
          task,
          functions,
          workcellInstruments,
          driversByNameAndVersion
        )
      );
    }
  }

  return graphEdges;
};
